/**
 * The motion scale: three durations, five curves, five springs and the
 * choreography that spaces them, authored.
 *
 * **This file used to be GENERATED**, emitted from the frozen theme fixture by
 * `scripts/generate-carried-tokens.ts`. It is not any more, and neither is
 * `colors.ts`: type is the one scale still carried over unchanged and
 * transcribed by the generator; motion is authored, like `spacing.ts` and
 * `radius.ts`, so it is written here and the generator no longer emits it. `alluzo-runtime.test.ts` asserts that it has
 * really left — the fixture still holds the carried motion leaf and nothing may
 * reproduce it.
 *
 * The rule, and the reason for each value, are in
 * `docs/design-system/foundations/motion.md`. Two things that file
 * cannot hold, because it is the contract rather than the evidence, are worth
 * repeating where the values live:
 *
 * **What was inherited, and what it really was.** The eight delays are gone —
 * they ran to 5,000ms and nothing in either tree ever read one. Five of the
 * eight durations are gone with them. Three easings are gone: `emphasized` and
 * `shake` had no reader, and `shake` is not even a shake (its curve is
 * monotonic, so it cannot cross its own start), and `overshoot` peaks at 1.102
 * — on the spinner's 0→360° rotation that is 396.7°, reached at 75% of every
 * 960ms turn and eased back, which is the stutter a loop reads as.
 *
 * **`linear` was not broken, and the contract used to say it was.** It was
 * written `bezier(0, 0, 0, 0)`, which is not how anybody spells linear — but
 * with both control points at the origin a cubic Bézier has x(t) = y(t) = t³,
 * so y = x, and it is the identity. Reanimated short-circuits it to
 * `LinearEasing` outright (`Bezier.ts` returns early when `x1 === y1 &&
 * x2 === y2`), and so does its native CSS path. It is re-pointed to
 * `(0, 0, 1, 1)` **for the reader, not for the runtime**: a value that is only
 * correct because of a short-circuit is a landmine for anyone who interpolates
 * the points rather than evaluating the curve.
 *
 * **An easing is its four control points and nothing else.** `Easing.bezier`
 * from Reanimated is how a platform spends them. That conversion happens once,
 * in `internal/motion-runtime.ts`, and `motion.enforcement.test.ts` is what
 * keeps it there.
 *
 * **Motion is scheme-independent**, so this object is exported from
 * `@alluzo/ui` directly, for the same reason `alluzoSpacing` and `alluzoRadius`
 * are: a module-scope `StyleSheet.create` has no hook to call.
 */

/**
 * How long a change takes, when nothing moves in space.
 *
 * **Durations are for change IN PLACE** — an opacity, a colour, a value
 * sweeping a track. Anything that moves or resizes takes a spring instead, and
 * `alluzoSpring` below says why. That split is the whole shape of this
 * foundation: two mechanisms, chosen by what actually changes, rather than one
 * ladder asked to do both jobs.
 *
 * **Three, bounded by perception.** Below about a hundred milliseconds a change
 * reads as a jump, and above about a third of a second it reads as a wait, so
 * the longest effect is 320 and that ceiling holds.
 *
 *   leave    something goes away — always faster than the thing arriving
 *   enter    something arrives, or changes in place — THE DEFAULT
 *   travel   a value crosses a distance the eye is meant to follow
 *
 * `leave` at 120 keeps the one asymmetry the app argued for in writing: a pill
 * leaving is faster than a pill arriving, because the gap it leaves closes
 * while it is still fading and a slow fade there reads as a stall.
 *
 * **`travel` is the exception to snappy, on purpose.** It is the onboarding
 * progress bar crossing the screen, and a progress bar that snaps reads as
 * broken rather than fast.
 */
export const alluzoDuration = {
  leave: 120,
  enter: 200,
  travel: 320,
} as const satisfies Readonly<Record<string, number>>;

export type DurationName = keyof typeof alluzoDuration;

/** A change with no argument for being anything else takes this. */
export const DEFAULT_DURATION: DurationName = 'enter';

/**
 * The shape of a change over its duration: four control points, nothing more.
 *
 *   standard   a colour changing, present at both ends — DEFAULT
 *   out        every enter and every exit: full speed at once, lands soft
 *   inOut      something moving on screen that cannot be a spring
 *   breathe    a reversing loop, and only that. Symmetric, so the turn is even
 *   linear     a non-reversing loop, and only that. The wrap must not be seen
 *
 * **There is no ease-in.** A curve that starts slowly makes the interface feel
 * as if it hesitated after the finger, and that is true of a leave as much as
 * an arrival: `out` answers both, so the thing going away is already moving in
 * the frame the finger lifts. `scales.doc.test.ts` refuses any curve whose
 * second control point leaves no deceleration at the end.
 *
 * `out` is a strong ease-out (Emil Kowalski's `[0.23, 1, 0.32, 1]`); `inOut`
 * is his strong ease-in-out, for a thing that crosses the screen and is on
 * screen at both ends. `breathe` is CSS `ease-in-out`, there so a loop can name
 * a token: a composed easing function is not four control points.
 */
export const alluzoEasing = {
  standard: { points: [0.3, 0, 0.2, 1] },
  out: { points: [0.23, 1, 0.32, 1] },
  inOut: { points: [0.77, 0, 0.175, 1] },
  breathe: { points: [0.42, 0, 0.58, 1] },
  linear: { points: [0, 0, 1, 1] },
} as const satisfies Readonly<Record<string, { points: readonly [number, number, number, number] }>>;

export type EasingName = keyof typeof alluzoEasing;

/** A change with no argument for being anything else takes this. */
export const DEFAULT_EASING: EasingName = 'standard';

/**
 * How a change that MOVES behaves: a spring, written as a perceptual duration
 * and a damping ratio.
 *
 * **A spring is interruptible, and that is why space takes one.** A timed curve
 * restarted in flight re-reads its start value and re-runs its ease from zero
 * velocity, so a pill tapped twice in a quarter of a second stutters. A spring
 * carries the velocity it already had, so the second change continues the
 * first.
 *
 * **`{ duration, dampingRatio }`, Apple's shape.** Apple describes a spring as
 * a perceptual duration and a bounce, and bounce is `1 − dampingRatio`. A ratio
 * of 1 is critically damped: the fastest a spring settles without overshoot.
 * The duration is PERCEPTUAL and the actual settle is about 1.5x it, which is
 * why springs are measured against the 200–500 class allowed for sheets and
 * drawers rather than the 320 ceiling on effects.
 *
 *   instant  120  1     under the finger: press-in, a switch thumb, a lift
 *   snappy   250  0.95  small things: a chip, an indicator, a menu, a row stepping aside
 *   smooth   350  1     THE DEFAULT: reflow, layout, money, a grid slot, scroll into view
 *   fluid    450  0.85  surfaces: a sheet, the orb travelling, a drag released with velocity
 *   lively   500  0.7   rare moments only: saved, done, first entry
 *
 * **Bounce is a budget.** Money (an amount, a total, a ledger row, a chart)
 * moves only on `smooth`, `instant` or an effects curve; `fluid` and `lively`
 * are spent on surfaces and rare moments. `motion.enforcement.test.ts` holds
 * the money half.
 *
 * A layout animation spells these `.springify(duration).dampingRatio(ratio)`;
 * `withSpring` takes the object as it stands. `springFor` and `springyLayout`
 * in `internal/motion-runtime.ts` are the one place either is written.
 */
export const alluzoSpring = {
  instant: { duration: 120, dampingRatio: 1 },
  snappy: { duration: 250, dampingRatio: 0.95 },
  smooth: { duration: 350, dampingRatio: 1 },
  fluid: { duration: 450, dampingRatio: 0.85 },
  lively: { duration: 500, dampingRatio: 0.7 },
} as const satisfies Readonly<Record<string, { duration: number; dampingRatio: number }>>;

export type SpringName = keyof typeof alluzoSpring;

/** A thing that moves, with no argument for being anything else, takes this. */
export const DEFAULT_SPRING: SpringName = 'smooth';

/**
 * How several changes are spaced, and how far and how much a thing travels.
 *
 *   stagger.tight   30   between siblings arriving close together (a list)
 *   stagger.loose   50   between groups the eye should read in turn
 *   offset.near      8   how far a thing travels arriving nearby
 *   offset.far      24   how far a surface travels arriving from its edge
 *   scale.press   0.97   a control up to 56 tall under the finger
 *   scale.enter   0.96   where a thing that grows in starts — never below 0.95
 *   scale.lift    1.02   a tile held in edit mode
 *
 * A stagger is milliseconds, an offset is points, a scale is a factor. None is
 * a duration, so none may be spent as one.
 */
export const alluzoChoreography = {
  stagger: { tight: 30, loose: 50 },
  offset: { near: 8, far: 24 },
  scale: { press: 0.97, enter: 0.96, lift: 1.02 },
} as const;

export type StaggerName = keyof typeof alluzoChoreography.stagger;

/**
 * Motion, as the theme serves it.
 *
 * There is no `delay` family and no `period` family, and both absences are
 * decisions. A delay is a stagger, spent through `alluzoChoreography`. A loop
 * period is not a duration — it is a property of one drawing, like the
 * fourteen bars in the voice wave — so it lives in the file that draws the
 * thing, named `PERIOD_MS`. *Motion* says why, and `motion.enforcement.test.ts`
 * holds it.
 */
export const alluzoMotion = {
  duration: alluzoDuration,
  easing: alluzoEasing,
  spring: alluzoSpring,
  choreography: alluzoChoreography,
} as const;
